import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShoppingBag, Plus, Minus, ChevronLeft, Beaker, Leaf, ShieldCheck, Truck, Star,
  MessageCircle, Check, Lock, BadgeCheck,
} from "lucide-react";
import api from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { fmt, proxyImg } from "../lib/format";
import ProductCard from "../components/ProductCard";
import SEO, { SITE_URL } from "../components/SEO";

const WHATS = "https://wa.me/5519992923017?text=Ol%C3%A1%20Biofarmax!%20Quero%20mais%20detalhes%20sobre%20um%20produto";

export default function Product() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("desc");
  const [imgIdx, setImgIdx] = useState(0);
  const [related, setRelated] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    setP(null); setQty(1); setImgIdx(0);
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      if (r.data.category_slugs?.[0]) {
        api.get(`/products?category=${r.data.category_slugs[0]}&limit=8`).then((rr) =>
          setRelated(rr.data.filter((x) => x.slug !== slug).slice(0, 4))
        );
      }
    });
  }, [slug]);

  if (!p) return (
    <div className="pt-32 pb-24 max-w-7xl mx-auto px-5 md:px-10">
      <div className="grid lg:grid-cols-2 gap-12">
        <div className="aspect-square rounded-3xl bg-bg-secondary animate-pulse" />
        <div className="space-y-4">
          <div className="h-12 bg-bg-secondary rounded animate-pulse" />
          <div className="h-6 bg-bg-secondary rounded animate-pulse w-2/3" />
          <div className="h-6 bg-bg-secondary rounded animate-pulse w-1/2" />
        </div>
      </div>
    </div>
  );

  const images = p.images?.length ? p.images : [p.thumbnail];
  const pixPrice = (p.price * 0.95);

  // Resolve absolute image URL for OG/Schema
  const resolveImg = (src) => {
    if (!src) return `${SITE_URL}/assets/biofarmax-logo.png`;
    if (src.startsWith("http")) return src;
    return `${SITE_URL}${src.startsWith("/") ? "" : "/"}${src}`;
  };
  const seoImg = resolveImg(p.thumbnail || images[0]);
  // Build a richer meta-description when product.short_description is too short for SEO.
  // Strategy: combine name + category + first benefit(s) until we reach ~140 chars.
  const buildSeoDescription = () => {
    const raw = (p.short_description || p.description || "").trim();
    if (raw && raw.length >= 80) return raw.slice(0, 240);
    const cat = p.categories?.[0] || "manipulado";
    const benefits = (p.beneficios || []).slice(0, 2).join(" · ");
    const parts = [
      `${p.name} — ${cat} Biofarmax`,
      raw,
      benefits,
      "Manipulado com qualidade farmacêutica · entrega para todo o Brasil.",
    ].filter(Boolean);
    return parts.join(". ").slice(0, 240);
  };
  const seoDescription = buildSeoDescription();

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": p.name,
    "description": seoDescription,
    "image": images.map(resolveImg),
    "sku": p.sku || `BFX-${p.id}`,
    "brand": { "@type": "Brand", "name": "Biofarmax" },
    "category": p.categories?.[0] || "Manipulados",
    "offers": {
      "@type": "Offer",
      "url": `${SITE_URL}/produto/${p.slug}`,
      "priceCurrency": p.currency || "BRL",
      "price": p.price.toFixed(2),
      "availability": p.is_in_stock
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": { "@type": "Organization", "name": "Biofarmax" },
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "128",
    },
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Início", "item": `${SITE_URL}/` },
      { "@type": "ListItem", "position": 2, "name": "Loja", "item": `${SITE_URL}/loja` },
      ...(p.category_slugs?.[0] && p.categories?.[0]
        ? [{ "@type": "ListItem", "position": 3, "name": p.categories[0], "item": `${SITE_URL}/categoria/${p.category_slugs[0]}` }]
        : []),
      { "@type": "ListItem", "position": p.category_slugs?.[0] ? 4 : 3, "name": p.name, "item": `${SITE_URL}/produto/${p.slug}` },
    ],
  };

  return (
    <div className="pt-20 sm:pt-24 pb-24 gradient-soft" data-testid="product-page">
      <SEO
        path={`/produto/${p.slug}`}
        title={p.name}
        description={seoDescription}
        image={seoImg}
        type="product"
        jsonLd={[productJsonLd, breadcrumbJsonLd]}
      />
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <Link to="/loja" className="inline-flex items-center gap-2 text-sm text-ink-secondary hover:text-brand-primary mb-8 font-tech" data-testid="back-to-shop">
          <ChevronLeft size={18} /> Voltar ao catálogo
        </Link>

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16">
          {/* GALLERY */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4 lg:sticky lg:top-28 self-start"
          >
            <div className="aspect-square rounded-[2rem] p-8 relative overflow-hidden grain shadow-[0_40px_80px_-30px_rgba(15,15,15,0.18)]"
                 style={{
                   background:
                     "radial-gradient(120% 90% at 50% 110%, rgba(201,180,122,0.15) 0%, transparent 55%), linear-gradient(180deg, #FFFFFF 0%, #F5F2E8 100%)",
                 }}>
              <img src={proxyImg(images[imgIdx])} alt={p.name} className="w-full h-full object-contain" data-testid="product-main-image" />
              <div className="absolute top-5 right-5 badge-pill bg-brand-accent text-white">
                <BadgeCheck size={11} strokeWidth={2.4} /> Manipulado
              </div>
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-3">
                {images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIdx(i)}
                    className={`aspect-square rounded-2xl border-2 p-2 bg-white transition-all ${
                      i === imgIdx ? "border-brand-accent shadow-[0_10px_24px_-10px_rgba(201,180,122,0.5)]" : "border-transparent hover:border-brand-primary/20"
                    }`}
                    data-testid={`thumb-${i}`}
                  >
                    <img src={proxyImg(src)} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* DETAILS */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="eyebrow mb-3">{p.categories?.join(" · ") || "Manipulação"}</div>
            <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl mb-5" data-testid="product-name">{p.name}</h1>

            {/* rating */}
            <div className="flex items-center gap-2 mb-6">
              <div className="star-row">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="#C9B47A" strokeWidth={1.2} />)}
              </div>
              <span className="text-sm text-ink-secondary">4.9 · 128 avaliações</span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-4 mb-2">
              <span className="font-heading text-5xl text-brand-primary" data-testid="product-price">{fmt(p.price)}</span>
              <span className="text-sm text-ink-secondary line-through">{fmt(p.price * 1.15)}</span>
            </div>
            <div className="mb-7 text-sm flex flex-wrap items-center gap-3">
              <span className="badge-pill bg-brand-accent/10 text-brand-accent">PIX 5% OFF · {fmt(pixPrice)}</span>
              <span className="text-ink-secondary">ou 6× sem juros</span>
            </div>

            {p.short_description && (
              <p className="text-ink-secondary text-lg leading-relaxed mb-8">{p.short_description}</p>
            )}

            {/* Benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-8">
              {(p.beneficios?.length ? p.beneficios : [
                "Manipulado com matérias-primas premium",
                "Fórmula personalizada à sua necessidade",
                "Embalagem com lacre de segurança",
                "Acompanhamento farmacêutico",
              ]).map((b, i) => (
                <div key={i} className="flex items-center gap-2 text-sm" data-testid={`benefit-${i}`}>
                  <Check size={16} className="text-brand-accent flex-shrink-0" strokeWidth={2.2} />
                  <span>{b}</span>
                </div>
              ))}
            </div>

            {/* Quantity + CTA */}
            <div className="flex flex-col sm:flex-row items-stretch gap-3 mb-5">
              <div className="flex items-center gap-1 bg-white border border-brand-primary/10 rounded-full p-1.5">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5 hover:bg-bg-secondary rounded-full" data-testid="qty-decrease">
                  <Minus size={16} />
                </button>
                <span className="w-10 text-center font-tech" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="p-2.5 hover:bg-bg-secondary rounded-full" data-testid="qty-increase">
                  <Plus size={16} />
                </button>
              </div>
              <button onClick={() => add(p, qty)} className="btn-primary flex-1" data-testid="add-to-cart-btn">
                <ShoppingBag size={18} /> Adicionar ao carrinho
              </button>
            </div>
            <a href={WHATS} target="_blank" rel="noreferrer" className="btn-whatsapp w-full sm:w-auto" data-testid="product-whatsapp-cta">
              <MessageCircle size={18} /> Falar com farmacêutico
            </a>

            {/* Trust strip */}
            <div className="mt-10 grid grid-cols-3 gap-3">
              {[
                { Icon: Beaker,        t: "Manipulado",   s: "CRF-SP" },
                { Icon: Lock,          t: "Compra segura", s: "SSL 256" },
                { Icon: Truck,         t: "Frete rápido", s: "Brasil" },
              ].map((b, i) => (
                <div key={i} className="text-center p-4 rounded-2xl bg-white border border-brand-primary/10" data-testid={`trust-${i}`}>
                  <b.Icon size={20} className="mx-auto mb-2 text-brand-accent" strokeWidth={1.5} />
                  <div className="text-[11px] font-semibold uppercase tracking-wider font-tech">{b.t}</div>
                  <div className="text-[10px] text-ink-secondary mt-0.5">{b.s}</div>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="border-t border-brand-primary/10 pt-8 mt-10">
              <div className="flex gap-6 mb-6 flex-wrap">
                {[
                  ["desc", "Descrição"],
                  ...(p.modo_de_uso ? [["uso", "Modo de uso"]] : []),
                  ...(p.ingredientes ? [["ing", "Ingredientes"]] : []),
                  ...(p.indicacoes ? [["ind", "Indicações"]] : []),
                  ["info", "Informações"],
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => setTab(k)}
                    className={`pb-2 font-tech text-sm uppercase tracking-wider transition border-b-2 ${
                      tab === k ? "border-brand-accent text-brand-primary" : "border-transparent text-ink-secondary"
                    }`}
                    data-testid={`tab-${k}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              {tab === "desc" && (
                <div className="prose prose-sm max-w-none text-ink-secondary whitespace-pre-line leading-relaxed" data-testid="tab-content-desc">
                  {p.description || p.short_description || "Produto manipulado em nossa farmácia com matérias-primas selecionadas."}
                </div>
              )}
              {tab === "uso" && (
                <div className="text-ink-secondary whitespace-pre-line leading-relaxed" data-testid="tab-content-uso">
                  {p.modo_de_uso}
                </div>
              )}
              {tab === "ing" && (
                <div className="text-ink-secondary leading-relaxed" data-testid="tab-content-ing">
                  {p.ingredientes}
                </div>
              )}
              {tab === "ind" && (
                <div className="text-ink-secondary whitespace-pre-line leading-relaxed" data-testid="tab-content-ind">
                  {p.indicacoes}
                </div>
              )}
              {tab === "info" && (
                <dl className="grid grid-cols-2 gap-y-3 text-sm" data-testid="tab-content-info">
                  <dt className="text-ink-secondary">SKU</dt><dd>{p.sku || "—"}</dd>
                  <dt className="text-ink-secondary">Categoria</dt><dd>{p.categories?.join(", ") || "—"}</dd>
                  <dt className="text-ink-secondary">Disponibilidade</dt>
                  <dd className={p.is_in_stock ? "text-status-success" : "text-status-error"}>
                    {p.is_in_stock ? "Em estoque" : "Indisponível"}
                  </dd>
                  <dt className="text-ink-secondary">Manipulação</dt><dd>Sob receita</dd>
                  <dt className="text-ink-secondary">Origem</dt><dd>Biofarmax · Campinas/SP</dd>
                </dl>
              )}
            </div>
          </motion.div>
        </div>

        {related.length > 0 && (
          <section className="mt-32">
            <div className="mb-10">
              <div className="eyebrow mb-3">Você também pode gostar</div>
              <h2 className="h-section text-3xl sm:text-4xl lg:text-5xl">Produtos relacionados</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((r, i) => (
                <ProductCard key={r.id} product={r} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
